import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { verifyEmailChange } from '../../../services/profile'
import { useTheme } from '../../../context/ThemeContext'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'

interface TokenData {
  id: string
  newEmail: string
  oldEmail: string
  type: string
  iat: number
  exp: number
}

export function VerifyEmailChange(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { currentUser, logout } = useAuth()

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [shouldLogout, setShouldLogout] = useState(false)

  const token = searchParams?.get('token')

  // Function to decode JWT token and extract payload
  const decodeToken = (token: string): TokenData | null => {
    try {
      const payload = token?.split('.')[1]
      // Convert base64url to base64 and add padding if needed
      const base64 = payload?.replace(/-/g, '+')?.replace(/_/g, '/')
      const padded = base64 + '='.repeat((4 - (base64?.length % 4)) % 4)
      return JSON?.parse(atob(padded))
    } catch (error) {
      return null
    }
  }

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error')
        setMessage('Verification token is missing. Please check your email link.')
        return
      }

      // Decode token to get oldEmail
      const tokenData = decodeToken(token)
      if (!tokenData) {
        setStatus('error')
        setMessage('Invalid token format. Please check your email link.')
        return
      }

      // Check if user is logged in
      if (currentUser?.data?.user?.email) {
        // User is logged in - check if their email matches oldEmail from token
        if (currentUser?.data?.user?.email?.toLowerCase() !== tokenData?.oldEmail?.toLowerCase()) {
          // Email doesn't match - redirect to 404
          navigate('/error/404')
          return
        }
        // Email matches - proceed with verification and flag for logout
        setShouldLogout(true)
            
      }

      try {
      
        const response: any = await verifyEmailChange(token)
        if (response?.data?.success) {
          setStatus('success')
          if ((currentUser?.data?.user?.email?.toLowerCase() !== tokenData?.oldEmail?.toLowerCase()) || currentUser?.data?.user?.email) {
            setMessage(response?.data?.message || 'Your email has been changed successfully! Please login with your new credentials.')
            // Logout the user after successful verification
            setTimeout(() => {
              logout()
              navigate('/auth')
            }, 5000)
          } else {
            setMessage(response?.data?.message || 'Your email has been changed successfully!')
            setTimeout(() => {
              navigate('/auth')
            }, 5000)
          }
        } else {
          setStatus('error')
          setMessage(response?.data?.message || 'Email verification failed. Please try again.')
        }
      } catch (error: any) {
        setStatus('error')
        const errorMessage =
          error?.response?.data?.message ||
          'Invalid or expired token. Please request a new email change.'
        setMessage(errorMessage)
      }
    }

    verifyEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser])

  return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
      <div className="text-center w-100" style={{ maxWidth: '500px' }}>
        <div className="text-center mb-10">
        <div className="d-flex align-items-center justify-content-center gap-5">
          <h1 className="text-dark mt-7">
            <span className={theme === 'dark' ? 'white__text' : ''}>Welcome to</span>{' '}
            <span className={`fw-semibold ${theme === 'dark' ? 'white__text' : ''}`}>b4i</span>
            <span className="text-primary fw-semibold">go</span>
          </h1>
          <a  className="">
            <img
              alt="Logo"
              src={toAbsoluteUrl('/media/logos/b4igo-go.png')}
              className="h-100px w-90px my-5 me-15"
            />
          </a>
        </div>
      </div>
      <h2 className={`fw-bolder fs-2 mb-5 ${theme === 'dark' ? 'white__text' : 'text-dark'}`}>
        Email Change Verification
      </h2>

      {status === 'loading' && (
        <div className="py-10">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={`mt-5 fs-5 ${theme === 'dark' ? 'white__text' : 'text-muted'}`}>
            Verifying your email change request...
          </p>
        </div>
      )}

      {status === 'success' && (
        <div className="py-10">
          <div className="mb-5">
            <i
              className="bi bi-check-circle-fill text-success"
              style={{ fontSize: 'var(--icon-6xl)' }}
            ></i>
          </div>
          <div className="alert alert-success" role="alert">
            <h4 className="alert-heading">Success!</h4>
            <p className="mb-0">{message}</p>
          </div>
          <p className={`mt-5 fs-6 ${theme === 'dark' ? 'white__text' : 'text-muted'}`}>
            {shouldLogout
              ? 'You will be logged out and redirected to login. Please use your new credentials to sign in.'
              : 'You will be redirected to the login page in 5 seconds...'}
          </p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => {
              if (shouldLogout) {
                logout()
              }
              navigate('/auth')
            }}
          >
            Go to Login
          </button>
        </div>
      )}

      {status === 'error' && (
        <div className="py-10">
          <div className="mb-5">
            <i
              className="bi bi-x-circle-fill text-danger"
              style={{ fontSize: 'var(--icon-6xl)' }}
            ></i>
          </div>
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Verification Failed</h4>
            <p className="mb-0">{message}</p>
          </div>
          <div className="mt-5">
            <button
              className="btn btn-primary"
              onClick={() => navigate('/auth')}
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
